import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface CashbookEntry {
  id: string;
  entryDate: string;
  entryType: "receipt" | "payment" | "transfer" | "opening" | "adjustment";
  account: string;
  developmentId?: string;
  description?: string;
  reference?: string;
  debit: number;
  credit: number;
  balance: number;
  runningBalance?: number;
  createdAt: string;
}

interface AccountBalance {
  account: string;
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

const ACCOUNT_OPTIONS = ["bank", "cash", "ecocash", "swipe", "rtgs"];

const CashbookReport: React.FC = () => {
  const router = useRouter();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<CashbookEntry[]>([]);
  const [accountBalances, setAccountBalances] = useState<AccountBalance[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tabValue === 0) {
      fetchCashbookStatement();
    } else {
      fetchAccountBalances();
    }
  }, [tabValue, selectedAccount, fromDate, toDate]);

  const fetchCashbookStatement = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("vms_access_token");

      const params = new URLSearchParams();
      if (selectedAccount) params.append("account", selectedAccount);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);

      const response = await fetch(
        `${config.API_BASE_URL}/cashbook?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      } else {
        setError("Failed to fetch cashbook statement");
      }
    } catch (err) {
      setError("Error fetching cashbook data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountBalances = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("vms_access_token");

      const response = await fetch(
        `${config.API_BASE_URL}/cashbook/account-balances?asAtDate=${toDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setAccountBalances(data);
      } else {
        setError("Failed to fetch account balances");
      }
    } catch (err) {
      setError("Error fetching account balances");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (tabValue === 0) {
      fetchCashbookStatement();
    } else {
      fetchAccountBalances();
    }
  };

  const handleExport = () => {
    if (tabValue === 0) {
      // Export statement
      const csvContent = [
        ["Cashbook Statement"],
        ["Date Range:", `${fromDate} to ${toDate}`],
        ["Account:", selectedAccount || "All Accounts"],
        [],
        [
          "Date",
          "Type",
          "Account",
          "Description",
          "Reference",
          "Debit",
          "Credit",
          "Running Balance",
        ],
        ...entries.map((e) => [
          new Date(e.entryDate).toLocaleDateString(),
          e.entryType,
          e.account,
          e.description || "",
          e.reference || "",
          e.debit.toFixed(2),
          e.credit.toFixed(2),
          (e.runningBalance || 0).toFixed(2),
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cashbook-statement-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // Export balances
      const csvContent = [
        ["Account Balances as at", toDate],
        [],
        ["Account", "Total Debits", "Total Credits", "Balance"],
        ...accountBalances.map((ab) => [
          ab.account,
          ab.totalDebit.toFixed(2),
          ab.totalCredit.toFixed(2),
          ab.balance.toFixed(2),
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `account-balances-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const getEntryTypeColor = (type: string) => {
    switch (type) {
      case "receipt":
        return "success";
      case "payment":
        return "error";
      case "transfer":
        return "info";
      case "opening":
        return "primary";
      case "adjustment":
        return "warning";
      default:
        return "default";
    }
  };

  const renderSummaryCards = () => {
    const totalDebit = entries.reduce((sum, e) => sum + e.debit, 0);
    const totalCredit = entries.reduce((sum, e) => sum + e.credit, 0);
    const closingBalance =
      entries.length > 0 ? entries[entries.length - 1].runningBalance || 0 : 0;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card
            sx={{ bgcolor: "success.light", color: "success.contrastText" }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Debits (Receipts)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(totalDebit)}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "error.light", color: "error.contrastText" }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Credits (Payments)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(totalCredit)}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            sx={{
              bgcolor: closingBalance >= 0 ? "primary.light" : "warning.light",
              color:
                closingBalance >= 0
                  ? "primary.contrastText"
                  : "warning.contrastText",
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Closing Balance
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(closingBalance)}
                  </Typography>
                </Box>
                <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Transactions
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {entries.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedAccount || "All accounts"}
                  </Typography>
                </Box>
                <AccountBalanceIcon
                  sx={{ fontSize: 40, color: "text.secondary" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderCashbookTable = () => {
    if (entries.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No cashbook entries found for the selected criteria
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.100" }}>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id} hover>
                <TableCell>
                  {new Date(entry.entryDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={entry.entryType}
                    size="small"
                    color={getEntryTypeColor(entry.entryType) as any}
                  />
                </TableCell>
                <TableCell sx={{ textTransform: "uppercase" }}>
                  {entry.account}
                </TableCell>
                <TableCell>{entry.description || "-"}</TableCell>
                <TableCell>{entry.reference || "-"}</TableCell>
                <TableCell
                  align="right"
                  sx={{ color: entry.debit > 0 ? "success.main" : "inherit" }}
                >
                  {entry.debit > 0 ? formatCurrency(entry.debit) : "-"}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: entry.credit > 0 ? "error.main" : "inherit" }}
                >
                  {entry.credit > 0 ? formatCurrency(entry.credit) : "-"}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    color:
                      (entry.runningBalance || 0) >= 0
                        ? "success.main"
                        : "error.main",
                  }}
                >
                  {formatCurrency(entry.runningBalance || 0)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAccountBalancesTable = () => {
    if (accountBalances.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No account balances found
          </Typography>
        </Paper>
      );
    }

    const totalBalance = accountBalances.reduce(
      (sum, ab) => sum + ab.balance,
      0,
    );

    return (
      <>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {accountBalances.map((ab) => (
            <Grid item xs={12} sm={6} md={3} key={ab.account}>
              <Card
                sx={{
                  borderLeft: 4,
                  borderColor: ab.balance >= 0 ? "success.main" : "error.main",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase" }}
                  >
                    {ab.account}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color={ab.balance >= 0 ? "success.main" : "error.main"}
                  >
                    {formatCurrency(ab.balance)}
                  </Typography>
                  <Box display="flex" gap={2} mt={1}>
                    <Typography variant="caption" color="success.main">
                      Dr: {formatCurrency(ab.totalDebit)}
                    </Typography>
                    <Typography variant="caption" color="error.main">
                      Cr: {formatCurrency(ab.totalCredit)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper
          sx={{ p: 2, bgcolor: "primary.light", color: "primary.contrastText" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Total Cash Position</Typography>
            <Typography variant="h4" fontWeight="bold">
              {formatCurrency(totalBalance)}
            </Typography>
          </Box>
        </Paper>

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell>Account</TableCell>
                <TableCell align="right">Total Debits</TableCell>
                <TableCell align="right">Total Credits</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountBalances.map((ab) => (
                <TableRow key={ab.account} hover>
                  <TableCell
                    sx={{ textTransform: "uppercase", fontWeight: "bold" }}
                  >
                    {ab.account}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "success.main" }}>
                    {formatCurrency(ab.totalDebit)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "error.main" }}>
                    {formatCurrency(ab.totalCredit)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      color: ab.balance >= 0 ? "success.main" : "error.main",
                    }}
                  >
                    {formatCurrency(ab.balance)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ fontWeight: "bold" }}>TOTAL</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", color: "success.main" }}
                >
                  {formatCurrency(
                    accountBalances.reduce((s, ab) => s + ab.totalDebit, 0),
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", color: "error.main" }}
                >
                  {formatCurrency(
                    accountBalances.reduce((s, ab) => s + ab.totalCredit, 0),
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    color: totalBalance >= 0 ? "success.main" : "error.main",
                  }}
                >
                  {formatCurrency(totalBalance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <FinanceContainer>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => router.push("/finance")}
            >
              Back
            </Button>
            <Typography variant="h5" fontWeight="bold">
              Cashbook Report
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={
                tabValue === 0
                  ? entries.length === 0
                  : accountBalances.length === 0
              }
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Cashbook Statement" />
            <Tab label="Account Balances" />
          </Tabs>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {tabValue === 0 && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account</InputLabel>
                  <Select
                    value={selectedAccount}
                    label="Account"
                    onChange={(e) => setSelectedAccount(e.target.value)}
                  >
                    <MenuItem value="">All Accounts</MenuItem>
                    {ACCOUNT_OPTIONS.map((acc) => (
                      <MenuItem key={acc} value={acc}>
                        {acc.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : tabValue === 0 ? (
          <>
            {renderSummaryCards()}
            <Typography variant="h6" gutterBottom>
              Transactions
            </Typography>
            {renderCashbookTable()}
          </>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Account Balances as at {new Date(toDate).toLocaleDateString()}
            </Typography>
            {renderAccountBalancesTable()}
          </>
        )}
      </Box>
    </FinanceContainer>
  );
};

export default CashbookReport;
